import type { Character } from '../domain/character.ts';
import { validateCharacter } from '../domain/validateCharacter.ts';
import { migrateCharacterRecord } from '../domain/migrateCharacter.ts';
import type { PortableCharacterFile } from './format.ts';
import { PORTABLE_FORMAT } from './format.ts';
import { migrateToCurrent } from './migrations.ts';
import { validateCustomSkillLibrary, createCustomSkillLibrary } from '../domain/customSkill.ts';
import {
  validateCustomBackgroundLibrary,
  createCustomBackgroundLibrary,
} from '../domain/customBackground.ts';
import { emptyCustomContent } from './customDependencies.ts';
import {
  createCustomItemLibrary,
  validateCustomItemLibrary,
} from '../domain/customItem.ts';
import type { PortableCustomContent } from './customDependencies.ts';

export type ImportOutcome =
  | { ok: true; file: PortableCharacterFile; unknownSkillIds: string[] }
  | { ok: false; errors: string[] };

/**
 * Parses, migrates and validates an exported file. Nothing is written to the
 * library unless this returns ok: true, so a bad file cannot damage existing
 * characters.
 *
 * knownSkillIds lets the importer warn about content this installation does not
 * have, without rejecting the character outright.
 */
export function parsePortableFile(text: string, knownSkillIds: ReadonlySet<string>): ImportOutcome {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, errors: ['The file is not valid JSON.'] };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { ok: false, errors: ['The file does not contain a character record.'] };
  }
  const raw = parsed as Record<string, unknown>;

  if (raw['format'] !== PORTABLE_FORMAT) {
    return {
      ok: false,
      errors: [`Expected a "${PORTABLE_FORMAT}" file but found "${String(raw['format'])}".`],
    };
  }

  let migrated: Record<string, unknown>;
  try {
    migrated = migrateToCurrent(raw);
  } catch (err) {
    return { ok: false, errors: [(err as Error).message] };
  }

  // The embedded character carries its own schema version and is migrated
  // separately from the file envelope.
  const migratedCharacter = migrateCharacterRecord(migrated['character']);
  const report = validateCharacter(migratedCharacter);
  if (!report.valid) return { ok: false, errors: report.errors };

  // RULE DCC-PORT-002 - embedded custom content passes the SAME validators the
  // local libraries use. A package is an untrusted file from another machine;
  // holding it to a lower standard than a local file would make importing the
  // way to get malformed content into the catalogue.
  const contentErrors = validateEmbeddedContent(migrated['customContent']);
  if (contentErrors.length > 0) return { ok: false, errors: contentErrors };

  migrated['character'] = migratedCharacter;
  const character = migratedCharacter as Character;
  const unknownSkillIds = character.skills
    .map((s) => s.skillId)
    .filter((id) => !knownSkillIds.has(id));

  return { ok: true, file: migrated as unknown as PortableCharacterFile, unknownSkillIds };
}

/**
 * Checks the embedded definitions with the local validators.
 *
 * They validate LIBRARIES, so the lists are wrapped in an empty library first.
 * Reusing them rather than writing import-side checks is the point: one set of
 * rules about what a custom Skill may be, not two that can drift.
 */
export function validateEmbeddedContent(value: unknown): string[] {
  if (value === undefined || value === null) {
    return ['The file does not say what custom content it carries.'];
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return ['The custom content in this file is not in a readable shape.'];
  }
  const content = value as Record<string, unknown>;
  const errors: string[] = [];

  const skills = content['skills'];
  const attacks = content['attacks'];
  if (!Array.isArray(skills)) {
    errors.push('The custom Skills in this file are not a list.');
  }
  if (!Array.isArray(attacks)) {
    errors.push('The custom AttackProfiles in this file are not a list.');
  }
  if (Array.isArray(skills) && Array.isArray(attacks)) {
    const check = validateCustomSkillLibrary({
      ...createCustomSkillLibrary('1970-01-01T00:00:00.000Z'),
      skills,
      attacks,
    });
    if (!check.valid) errors.push(...check.problems.map((p) => `Custom Skill: ${p}`));
  }

  const items = content['items'];
  if (items !== undefined) {
    if (!Array.isArray(items)) {
      errors.push('The custom Items in this file are not a list.');
    } else {
      const check = validateCustomItemLibrary({
        ...createCustomItemLibrary('1970-01-01T00:00:00.000Z'),
        items,
      });
      if (!check.valid) errors.push(...check.problems.map((p) => `Custom Item: ${p}`));
    }
  }

  const backgrounds = content['backgrounds'];
  if (!Array.isArray(backgrounds)) {
    errors.push('The custom Backgrounds in this file are not a list.');
  } else {
    const check = validateCustomBackgroundLibrary({
      ...createCustomBackgroundLibrary('1970-01-01T00:00:00.000Z'),
      backgrounds,
    });
    if (!check.valid) errors.push(...check.problems.map((p) => `Custom Background: ${p}`));
  }

  return errors;
}

export function embeddedContentOf(file: PortableCharacterFile): PortableCustomContent {
  const content = file.customContent;
  if (!content) return emptyCustomContent();
  return {
    skills: Array.isArray(content.skills) ? content.skills : [],
    attacks: Array.isArray(content.attacks) ? content.attacks : [],
    backgrounds: Array.isArray(content.backgrounds) ? content.backgrounds : [],
    // Absent in schema 2 files written before custom Items existed.
    items: Array.isArray(content.items) ? content.items : [],
  };
}
